import { baseApi } from './base-api';
import { getIdentityGeneration } from '../utils/identity-scope';

export interface ApiUser {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  org_id: string;
  role: string;
  email_verified: boolean;
  settings?: {
    assistant_auto_execute?: boolean;
    [key: string]: unknown;
  };
}

export interface MeResponse {
  login: string;
  user: ApiUser | null;
}

export interface AuthResponse {
  response: MeResponse;
  type: string;
}

const meTag = { type: 'ApiUser' as const, id: 'ME' };

const meApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMe: builder.query<AuthResponse, void>({
      query: () => ({
        url: 'api/me',
        method: 'GET',
      }),
      providesTags: [meTag],
    }),

    updateMe: builder.mutation<AuthResponse, { updatedData: Partial<ApiUser> }>({
      query: ({ updatedData }) => ({
        url: 'api/me',
        method: 'PATCH',
        body: updatedData,
      }),
      transformResponse: (response, meta, arg) => {
        // Server returns `true`, so we return the data we just sent instead
        return { ...arg.updatedData } as Partial<ApiUser>;
      },
      // Optimistically update the cache to reflect the changes
      onQueryStarted: async ({ updatedData }, { dispatch, queryFulfilled }) => {
        const generation = getIdentityGeneration();
        const patchResult = dispatch(
          baseApi.util.updateQueryData('getMe', undefined, (draft) => {
            if (draft?.response.user) {
              draft.response.user = { ...draft.response.user, ...updatedData };
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          if (generation !== getIdentityGeneration()) return;
          patchResult.undo(); // Rollback the optimistic update if the mutation fails
        }
      },
      invalidatesTags: [meTag],
    }),
  }),
});

export const { useGetMeQuery, useUpdateMeMutation } = meApi;

export { meApi };
